/**
 * Link creation, editing and removal.
 *
 * Every anchor this module produces carries the same three attributes, on every
 * path -- the old editor produced bare `<a href>` from the toolbar but
 * `target="_blank"` from its autolinker, so the stored markup was inconsistent.
 * Removal exists at all here, which it did not before.
 */

import * as bookmark from './bookmark';
import {
  blockOf,
  closestWithin,
  isolate,
  markAncestor,
  mergeInlines,
  splitRangeBoundaries,
  splitTextAt,
  unwrap,
} from './dom-utils';
import { LINK_REL, LINK_TARGET, sanitizeUrl } from './schema';
import { displayUrl, findUrls } from './url';

const WORD_SEPARATORS = [' ', '\u00A0', '\t', '\n'];

function createAnchor(href: string): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', href);
  anchor.setAttribute('target', LINK_TARGET);
  anchor.setAttribute('rel', LINK_REL);
  return anchor;
}

/** The link the node sits inside, if any. Drives the link bubble. */
export function linkAt(root: HTMLElement, node: Node | null): HTMLAnchorElement | null {
  return closestWithin(node, root, (element) => element.nodeName === 'A') as HTMLAnchorElement | null;
}

/**
 * Applies a link to the selection, or edits the one the caret sits in.
 * Returns false when the URL is not usable -- notably `javascript:`, which the
 * old editor accepted and persisted.
 */
export function applyLink(root: HTMLElement, range: Range, href: string, label?: string): boolean {
  const safe = sanitizeUrl(href);
  if (!safe) {
    return false;
  }

  const existing = range.collapsed ? linkAt(root, range.startContainer) : null;
  if (existing) {
    existing.setAttribute('href', safe);
    existing.setAttribute('target', LINK_TARGET);
    existing.setAttribute('rel', LINK_REL);
    if (label && label !== existing.textContent) {
      existing.textContent = label;
    }
    return true;
  }

  if (range.collapsed) {
    const anchor = createAnchor(safe);
    anchor.textContent = label && label.length > 0 ? label : displayUrl(safe);
    range.insertNode(anchor);

    const selection = window.getSelection();
    if (selection) {
      const after = document.createRange();
      after.setStartAfter(anchor);
      after.collapse(true);
      selection.removeAllRanges();
      selection.addRange(after);
    }

    const block = blockOf(anchor, root);
    if (block) {
      mergeInlines(block);
    }
    return true;
  }

  const mark = bookmark.save(root);
  const leaves = splitRangeBoundaries(root, range);
  const touchedBlocks = new Set<Element>();

  for (const leaf of leaves) {
    const block = blockOf(leaf, root);
    if (block) {
      touchedBlocks.add(block);
    }
    const previous = markAncestor(leaf, root, 'A');
    if (previous) {
      unwrap(isolate(leaf, previous));
    }
    const anchor = createAnchor(safe);
    leaf.before(anchor);
    anchor.appendChild(leaf);
  }

  for (const block of touchedBlocks) {
    mergeInlines(block);
  }

  bookmark.restore(root, mark);
  return true;
}

/**
 * Removes the link the caret sits in, or unlinks everything the selection
 * covers. A collapsed caret removes the whole link, which is the behaviour
 * authors expect and the operation the old editor had no way to perform.
 */
export function removeLink(root: HTMLElement, range: Range): void {
  const mark = bookmark.save(root);

  if (range.collapsed) {
    const anchor = linkAt(root, range.startContainer);
    if (anchor) {
      const block = blockOf(anchor, root);
      unwrap(anchor);
      if (block) {
        mergeInlines(block);
      }
    }
    bookmark.restore(root, mark);
    return;
  }

  const leaves = splitRangeBoundaries(root, range);
  const touchedBlocks = new Set<Element>();

  for (const leaf of leaves) {
    const block = blockOf(leaf, root);
    if (block) {
      touchedBlocks.add(block);
    }
    const anchor = markAncestor(leaf, root, 'A');
    if (anchor) {
      unwrap(isolate(leaf, anchor));
    }
  }

  for (const block of touchedBlocks) {
    mergeInlines(block);
  }

  bookmark.restore(root, mark);
}

/**
 * Links the word immediately before `boundary` in `text`, if it is a URL.
 *
 * Unlike the old implementation, this trims the matched run out with two
 * `splitText` calls and inserts the anchor between the halves, so the caller
 * gets back the live text node that follows the link. The caret goes there --
 * outside the anchor -- which is what stops the link from swallowing the rest
 * of the sentence.
 */
export function autolinkBefore(root: HTMLElement, text: Text, boundary: number): Text | null {
  if (markAncestor(text, root, 'A')) {
    return null;
  }

  const upto = text.data.slice(0, boundary);
  const wordStart = Math.max(...WORD_SEPARATORS.map((separator) => upto.lastIndexOf(separator))) + 1;

  const word = upto.slice(wordStart);
  if (word.length === 0) {
    return null;
  }

  const match = findUrls(word).find(
    (candidate) => candidate.start === 0 && candidate.end === word.length,
  );
  if (!match) {
    return null;
  }

  const linkText = splitTextAt(text, wordStart);
  const trailing = splitTextAt(linkText, match.text.length);

  const anchor = createAnchor(match.href);
  linkText.before(anchor);
  anchor.appendChild(linkText);

  return trailing;
}
